/** Column definitions for the notification-subscriptions grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget (the page also sets `disableColumnFilter`). Sortable
 * column FIELDS equal the backend ordering whitelist keys (id, tenant,
 * restech, account, type, status, mode, created_ts, last_received_ts) so the
 * sort model round-trips as the DRF `ordering` param — everything else is
 * `sortable: false` (the dead-sort-header bug class). Cell popovers carry
 * copy actions and quick filters appending an equality chip to the search.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';

import type { SubscriptionListItem } from '@/api/notificationSubscriptions';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TsValue } from '@/features/bgtasks/taskFormat';

import { AccountChip, ModeChip, StatusChip } from './subscriptionFormat';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

function copyAction(label: string, value: string): CellAction {
  return {
    key: 'copy',
    label,
    icon: <ContentCopyIcon fontSize="small" />,
    onClick: () => void navigator.clipboard.writeText(value),
  };
}

function filterAction(
  label: string,
  ns: string,
  value: string,
  onQuickFilter: BuildColumnsOptions['onQuickFilter'],
): CellAction {
  return {
    key: `filter-${ns}`,
    label,
    icon: <FilterAltIcon fontSize="small" />,
    onClick: () => onQuickFilter(ns, value),
  };
}

const dash = <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
  /** Append an exact `ns=value` chip to the search. */
  onQuickFilter: (namespace: string, value: string) => void;
  /** Route to a subscription's detail drawer (keeps the grid's query string). */
  detailPath: (id: number) => string;
}

export function buildColumns(opts: BuildColumnsOptions): GridColDef<SubscriptionListItem>[] {
  const { showTenant, onQuickFilter, detailPath } = opts;

  const cols: GridColDef<SubscriptionListItem>[] = [];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 150,
      filterable: false,
      valueGetter: (_v, row) => row.tenant?.code ?? '',
      renderCell: (p) =>
        p.row.tenant ? (
          <WithTenantMark environment={p.row.tenant.environment}>
            <CellActionsPopover
              auto
              title={p.row.tenant.name}
              actions={[
                {
                  key: 'open',
                  label: 'Open tour operator',
                  icon: <OpenInNewIcon fontSize="small" />,
                  href: `/tour-operators/${p.row.tenant.id}`,
                },
                filterAction('Filter by this tenant', 'tenant', String(p.row.tenant.id), onQuickFilter),
              ]}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              <Box component="span" sx={mono}>{p.row.tenant.code}</Box>
            </CellActionsPopover>
          </WithTenantMark>
        ) : (
          dash
        ),
    });
  }

  cols.push(
    {
      field: 'restech',
      headerName: 'Restech',
      width: 120,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[
            copyAction('Copy restech', p.row.restech),
            filterAction('Filter by this restech', 'restech', p.row.restech, onQuickFilter),
          ]}
        >
          <Box component="span" sx={mono}>{p.row.restech}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'account',
      headerName: 'Account',
      width: 120,
      filterable: false,
      valueGetter: (_v, row) => row.account || 'default',
      renderCell: (p) =>
        p.row.account ? (
          <CellActionsPopover
            auto
            actions={[
              copyAction('Copy account slug', p.row.account),
              filterAction('Filter by this account', 'account', p.row.account, onQuickFilter),
            ]}
          >
            <AccountChip account={p.row.account} />
          </CellActionsPopover>
        ) : (
          <AccountChip account="" />
        ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 175,
      filterable: false,
      valueGetter: (_v, row) => row.notification_type,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[
            filterAction('Filter by this type', 'type', p.row.notification_type, onQuickFilter),
          ]}
        >
          <Box component="span" sx={mono}>{p.row.notification_type}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      filterable: false,
      valueGetter: (_v, row) => row.status,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[
            filterAction('Filter by this status', 'status', p.row.status, onQuickFilter),
          ]}
        >
          <StatusChip status={p.row.status} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: 100,
      filterable: false,
      valueGetter: (_v, row) => row.mode,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          actions={[filterAction('Filter by this mode', 'mode', p.row.mode, onQuickFilter)]}
        >
          <ModeChip mode={p.row.mode} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'callback_url',
      headerName: 'Callback URL',
      flex: 1,
      minWidth: 220,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.callback_url ? (
          <CellActionsPopover
            auto
            title={p.row.callback_url}
            actions={[copyAction('Copy callback URL', p.row.callback_url)]}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <Box component="span" sx={mono}>{p.row.callback_url}</Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'remote_id',
      headerName: 'Remote ID',
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.remote_id ? (
          <CellActionsPopover
            auto
            title={p.row.remote_id}
            actions={[
              copyAction('Copy remote id', p.row.remote_id),
              filterAction('Filter by this remote id', 'remote', p.row.remote_id, onQuickFilter),
            ]}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <Box component="span" sx={mono}>{p.row.remote_id}</Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'last_received_ts',
      headerName: 'Last received',
      width: 165,
      filterable: false,
      renderCell: (p) =>
        p.row.last_received_ts ? <TsValue value={p.row.last_received_ts} /> : dash,
    },
    {
      field: 'last_error',
      headerName: 'Last error',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.last_error ? (
          <Tooltip title={p.row.last_error}>
            <Box
              component="span"
              sx={{ color: 'error.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p.row.last_error}
            </Box>
          </Tooltip>
        ) : (
          dash
        ),
    },
    {
      field: 'created_ts',
      headerName: 'Created',
      width: 165,
      filterable: false,
      renderCell: (p) => (p.row.created_ts ? <TsValue value={p.row.created_ts} /> : dash),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={String(p.row.id)}
          actions={[
            copyAction('Copy subscription id', String(p.row.id)),
            {
              key: 'open',
              label: 'Open subscription',
              icon: <OpenInNewIcon fontSize="small" />,
              href: detailPath(p.row.id),
            },
          ]}
        >
          <Box component="span" sx={mono}>{p.row.id}</Box>
        </CellActionsPopover>
      ),
    },
  );

  return cols;
}
